import { IntlShape } from 'react-intl'
import { PatrolInterval, PatrolSchedule } from '@/types/vlm'

export const PATROL_INTERVAL_SECONDS: Record<PatrolInterval, number> = {
  '15m': 15 * 60,
  '30m': 30 * 60,
  '1h': 3600,
  '2h': 2 * 3600,
  '3h': 3 * 3600,
  '4h': 4 * 3600,
  '6h': 6 * 3600,
  '8h': 8 * 3600,
  '12h': 12 * 3600,
  '24h': 24 * 3600
}

export const PATROL_INTERVALS = Object.keys(
  PATROL_INTERVAL_SECONDS
) as PatrolInterval[]

export const PATROL_MAX_TIMES = 24
// The backend takes seconds (60–7200); the form works in whole minutes.
export const PATROL_MIN_ROUND_TIMEOUT_MINUTES = 1
export const PATROL_MAX_ROUND_TIMEOUT_MINUTES = 120

const SECONDS_PER_DAY = 24 * 3600
const MINUTES_PER_DAY = 24 * 60
const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/

export const isValidPatrolTime = (value: string) => TIME_PATTERN.test(value)

export const roundTimeoutToSeconds = (minutes: number) => minutes * 60

// Rounds down, so a saved timeout never grows past the gap between rounds.
export const roundTimeoutToMinutes = (seconds: number) =>
  Math.max(PATROL_MIN_ROUND_TIMEOUT_MINUTES, Math.floor(seconds / 60))

type ScheduleTimes =
  | Pick<Extract<PatrolSchedule, { mode: 'at' }>, 'mode' | 'at'>
  | Pick<Extract<PatrolSchedule, { mode: 'every' }>, 'mode' | 'every'>

const toMinutes = (time: string) =>
  Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5))

const fromMinutes = (minutes: number) =>
  [Math.floor(minutes / 60), minutes % 60]
    .map((part) => String(part).padStart(2, '0'))
    .join(':')

// Every interval divides the day, so a start time plus an interval is sent as
// the equivalent fixed times: 08:35 every 8h gives 08:35, 16:35, 00:35. The
// backend's own interval mode has no start time; the form only keeps it for a
// schedule saved that way (see patrolLegacyEvery).
export const expandPatrolInterval = (
  start: string,
  interval: PatrolInterval
) => {
  const step = PATROL_INTERVAL_SECONDS[interval] / 60
  const first = toMinutes(start)

  return Array.from({ length: MINUTES_PER_DAY / step }, (_, i) =>
    fromMinutes((first + i * step) % MINUTES_PER_DAY)
  )
}

// The interval that fixed times are evenly spaced by, if any, so a
// saved interval opens as one again. The start is the day's first round, as
// the backend keeps the times sorted. A single time is left as a fixed time.
export const detectPatrolInterval = (at: string[]) => {
  const times = [...new Set(at)].filter(isValidPatrolTime).sort()
  if (times.length < 2 || times.length !== at.length) return null

  const interval = PATROL_INTERVALS.find(
    (option) =>
      PATROL_INTERVAL_SECONDS[option] * times.length === SECONDS_PER_DAY
  )
  if (!interval) return null

  const expected = [...expandPatrolInterval(times[0], interval)].sort()
  return expected.every((time, i) => time === times[i])
    ? { interval, start: times[0] }
    : null
}

// Shortest time between two consecutive rounds. For fixed times this includes
// the wrap-around from the day's last time to the next day's first; a single
// time gives a full day. Mirrors the backend's min_patrol_gap_seconds.
export const minPatrolGapSeconds = (schedule: ScheduleTimes) => {
  if (schedule.mode === 'every') return PATROL_INTERVAL_SECONDS[schedule.every]

  const minutes = [...new Set(schedule.at.filter(isValidPatrolTime))]
    .map(toMinutes)
    .sort((a, b) => a - b)

  if (minutes.length < 2) return SECONDS_PER_DAY

  const gaps = minutes.slice(1).map((m, i) => (m - minutes[i]) * 60)
  gaps.push((minutes[0] + 24 * 60 - minutes[minutes.length - 1]) * 60)

  return Math.min(...gaps)
}

// About 25 s of inference per camera, twice for headroom, over the pipeline
// manager's pull concurrency of 3 (vp-dashboard's heuristic). In minutes.
export const recommendedRoundTimeout = (cameraCount: number) =>
  Math.max(5, Math.ceil((2 * cameraCount * 25) / 3 / 60))

export const defaultRoundTimeout = (cameraCount: number, gapSeconds: number) =>
  Math.max(
    PATROL_MIN_ROUND_TIMEOUT_MINUTES,
    Math.min(
      Math.max(15, recommendedRoundTimeout(cameraCount)),
      gapSeconds / 60 - 1,
      PATROL_MAX_ROUND_TIMEOUT_MINUTES
    )
  )

export const formatDuration = (seconds: number, intl: IntlShape) => {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.round((seconds % 3600) / 60)

  const id = !hours ? 'minutes' : minutes ? 'hoursMinutes' : 'hours'

  return intl.formatMessage(
    { id: `vlm.pipelines.create.patrol.schedule.duration.${id}` },
    { hours, minutes }
  )
}

export const formatPatrolInterval = (
  interval: PatrolInterval,
  intl: IntlShape
) =>
  intl.formatMessage({
    id: `vlm.pipelines.create.patrol.schedule.interval.${interval}`
  })

const withTimezone = (text: string, timezone?: string) =>
  timezone ? `${text} (${timezone})` : text

export const formatPatrolIntervalFrom = (
  interval: PatrolInterval,
  start: string,
  timezone: string | undefined,
  intl: IntlShape
) =>
  withTimezone(
    intl.formatMessage(
      { id: 'vlm.pipelines.create.patrol.schedule.intervalFrom' },
      { interval: formatPatrolInterval(interval, intl), start }
    ),
    timezone
  )

export const formatPatrolSchedule = (
  schedule: ScheduleTimes & { timezone?: string },
  intl: IntlShape
) => {
  if (schedule.mode === 'every')
    return formatPatrolInterval(schedule.every, intl)

  const series = detectPatrolInterval(schedule.at)
  if (series)
    return formatPatrolIntervalFrom(
      series.interval,
      series.start,
      schedule.timezone,
      intl
    )

  const times = [...new Set(schedule.at)].sort().join(', ')
  return withTimezone(times, schedule.timezone)
}

const FALLBACK_TIMEZONES = [
  'UTC',
  'Europe/London',
  'Europe/Amsterdam',
  'Europe/Berlin',
  'Europe/Paris',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Warsaw',
  'Europe/Helsinki',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Asia/Dubai',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney'
]

// Not in the TS lib this project targets, but available in current browsers.
const supportedTimezones = (
  Intl as typeof Intl & {
    supportedValuesOf?: (key: 'timeZone') => string[]
  }
).supportedValuesOf

export const getTimezoneOptions = (...include: string[]) => {
  const zones = supportedTimezones?.('timeZone') ?? FALLBACK_TIMEZONES

  return [...new Set([...zones, ...include.filter(Boolean)])]
}
